<template>
  <div
    ref="host"
    class="shadow-html-host" />
</template>

<script>
import DOMPurify from 'dompurify'

// Fix broken table display in shadow DOM (email clients often use display:block on rows).
const TABLE_LAYOUT_FIX = `
          table {
            display: table;
            visibility: visible;
          }
          thead, tbody, tfoot {
            display: table-row-group;
            visibility: visible;
          }
          tr {
            display: table-row;
            visibility: visible;
          }
          th, td {
            display: table-cell;
            visibility: visible;
          }`

// MJML / template layout tables: keep border, cellpadding, cellspacing and inline styles.
// Covers table[border="0"], role="presentation", and backend wrapper cells (.border-0).
const EMAIL_NATIVE_TABLE_RESET = `
          table[border="0"] th,
          table[border="0"] td,
          table[role="presentation"] th,
          table[role="presentation"] td,
          .border-0,
          .border-0 th,
          .border-0 td {
            border: unset;
            border-width: unset;
            border-style: unset;
            border-color: unset;
          }`

// TinyMCE / user tables and calendar eventTable only (not layout tables).
const CONTENT_TABLE_SELECTOR = 'table:not([border="0"]):not([role="presentation"])'

export default {
  name: 'ShadowHtml',
  props: {
    html: {
      type: String,
      default: ''
    },
    injectDefaultStyles: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    html() {
      this.renderHtml()
    }
  },
  mounted() {
    this.renderHtml()
  },
  methods: {
    renderHtml(htmlOverride) {
      const host = this.$refs.host
      if (!host) return
      if (!this._shadowRoot) {
        this._shadowRoot = host.attachShadow({ mode: 'open' })
      }
      const html = typeof htmlOverride !== 'undefined' ? htmlOverride : this.html
      const sanitized = this.sanitize(html || '')
      const baseStyle = `<style>
          :host {
            display: block;
          }
          img {
            max-width: 100%;
            height: auto;
          }
          ${TABLE_LAYOUT_FIX}
        </style>`
      const optionalTableStyle = this.injectDefaultStyles
        ? `<style>
            ${CONTENT_TABLE_SELECTOR} {
              border-collapse: collapse;
              width: 100%;
              margin: 8px 0;
            }
            ${CONTENT_TABLE_SELECTOR} th,
            ${CONTENT_TABLE_SELECTOR} td {
              border: 0;
              padding: 3px 4px;
              text-align: left;
            }
            ${CONTENT_TABLE_SELECTOR} th {
              background-color: #f8f9fa;
              font-weight: bold;
            }
            table.eventTable tr td,
            table.eventTable tr th {
              border: 1px solid rgba(0, 0, 0, 0.2);
            }
            ${EMAIL_NATIVE_TABLE_RESET}
          </style>`
        : `<style>
            ${CONTENT_TABLE_SELECTOR} {
              border-collapse: collapse;
              max-width: 100%;
              margin: 8px 0;
            }
            ${CONTENT_TABLE_SELECTOR} th,
            ${CONTENT_TABLE_SELECTOR} td {
              border: 1px solid rgba(0, 0, 0, 0.2);
              padding: 3px 4px;
              text-align: left;
              vertical-align: top;
            }
            table.eventTable tr td,
            table.eventTable tr th {
              border: 1px solid rgba(0, 0, 0, 0.2);
            }
            ${EMAIL_NATIVE_TABLE_RESET}
          </style>`
      this._shadowRoot.innerHTML = baseStyle + optionalTableStyle + sanitized
    },
    setHtml(html) {
      this.renderHtml(html)
    },
    protectAngleBracketedEmails(htmlString) {
      if (!htmlString || typeof htmlString !== 'string') {
        return htmlString || ''
      }
      return htmlString.replace(/<([^\s>]*@[^\s>]*)>/g, '&lt;$1&gt;')
    },
    sanitize(input) {
      if (!input) return ''
      try {
        const withProtectedEmails = this.protectAngleBracketedEmails(input)
        return DOMPurify.sanitize(withProtectedEmails, {
          ALLOWED_TAGS: [
            'p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a', 'span', 'div',
            'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
            'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
            'blockquote', 'pre', 'code', 'hr', 'img', 'sup', 'sub'
          ],
          ALLOWED_ATTR: [
            'href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'style',
            'colspan', 'rowspan', 'width', 'height', 'align', 'valign', 'border', 'cellpadding', 'cellspacing', 'role'
          ]
        })
      } catch (e) {
        return input || ''
      }
    }
  }
}
</script>

<style scoped>
.shadow-html-host {
  display: block;
}
</style>
